<template>
  <div class="home">
    <div>
      <!-- header -->
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{$t('attendance_management')}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search_employee')">
              </div>
              <!-- Mobile filter toggle -->
              <button @click="showFilters = !showFilters" class="btn btn-outline-secondary d-md-none">
                <AppIcon name="bi-funnel" class="bi bi-funnel" />
              </button>
            </div>
          </div>
        </div>
      </section>
      <!-- header -->

      <!-- Summary Cards -->
      <section class="px-8 mb-4">
        <div class="row g-2">
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-success" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ stats.present }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('present')}}</small>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-x-circle-fill" class="bi bi-x-circle-fill text-danger" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ stats.absent }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('absent')}}</small>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-exclamation-circle-fill" class="bi bi-exclamation-circle-fill text-warning" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ stats.late }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('late')}}</small>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-calendar-event-fill" class="bi bi-calendar-event-fill text-info" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ stats.on_leave }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('on_leave')}}</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- table -->
      <section class="">
        <div class="px-8">
          <!-- Mobile action buttons -->
          <div class="d-flex d-md-none gap-2 mb-3">
            <button v-if="helper.checkAccess('create_attendances') != -1" @click="create" class="btn btn-sm btn-success flex-grow-1">
              <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
              <span>{{$t('record_attendance')}}</span>
            </button>
          </div>

          <!-- Filters section (collapsible on mobile, always visible on desktop) -->
          <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
            <!-- Date Range -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('from_date')}}</label>
              <input type="date" v-model="filters.from_date" class="form-control form-control-sm">
            </div>
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('to_date')}}</label>
              <input type="date" v-model="filters.to_date" class="form-control form-control-sm">
            </div>

            <!-- Status filter -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('status')}}</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">{{$t('all_statuses')}}</option>
                <option value="present">{{$t('present')}}</option>
                <option value="absent">{{$t('absent')}}</option>
                <option value="late">{{$t('late')}}</option>
                <option value="half_day">{{$t('half_day')}}</option>
                <option value="on_leave">{{$t('on_leave')}}</option>
              </select>
            </div>
            
            <!-- Spacer for desktop -->
            <div class="col d-none d-md-block"></div>
            
            <!-- Desktop action buttons -->
            <div class="col-md-2 d-none d-md-block" v-if="helper.checkAccess('create_attendances') != -1">
              <button @click="create" class="btn btn-sm btn-success w-100">
                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                <span>{{$t('record_attendance')}}</span>
              </button>
            </div>
          </div>

          <div class="page-table-container mb-6 bg-white rounded shadow">
            
            <!-- table -->
            <div class="table-responsive">
              <table class="table mb-0 table-borderless table-striped small align-middle">
                <thead>
                  <tr class="text-secondary">
                    <th class="pt-4 pb-3 px-6">{{$t('employee')}}</th>
                    <sortable-table-header field="date" label="date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchAttendances)" />
                    <sortable-table-header field="check_in" label="check_in" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchAttendances)" />
                    <sortable-table-header field="check_out" label="check_out" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchAttendances)" />
                    <sortable-table-header field="work_hours" label="work_hours" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchAttendances)" />
                    <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchAttendances)" />
                    <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="att in attendances" :key="att.id">
                    <td class="py-5 px-6">
                      <div class="d-flex align-items-center">
                        <div class="avatar-circle me-2 bg-primary text-white d-flex align-items-center justify-content-center" style="width: 32px; height: 32px; border-radius: 50%; font-size: 12px;">
                          <img v-if="att.employee && att.employee.image" :src="att.employee.image" class="w-100 h-100 rounded-circle" style="object-fit: cover;">
                          <span v-else>{{ getInitials(att.employee ? att.employee.name : '?') }}</span>
                        </div>
                        <router-link v-if="att.employee" :to="`/hr/employees/${att.employee.id}`" class="text-decoration-none text-dark fw-bold">
                          {{att.employee.name}}
                        </router-link>
                        <span v-else>-</span>
                      </div>
                    </td>
                    <td class="py-5 px-6">{{formatDate(att.date)}}</td>
                    <td class="py-5 px-6">
                      <span v-if="att.check_in" class="badge bg-light text-dark border">{{formatTime(att.check_in)}}</span>
                      <span v-else>-</span>
                    </td>
                    <td class="py-5 px-6">
                      <span v-if="att.check_out" class="badge bg-light text-dark border">{{formatTime(att.check_out)}}</span>
                      <span v-else>-</span>
                    </td>
                    <td class="py-5 px-6 fw-bold text-primary">{{att.work_hours || '-'}} {{att.work_hours ? 'hrs' : ''}}</td>
                    <td class="py-5 px-6">
                      <span class="badge rounded-pill" :class="{
                        'bg-success': att.status === 'present',
                        'bg-danger': att.status === 'absent',
                        'bg-warning': att.status === 'late',
                        'bg-info': att.status === 'half_day',
                        'bg-secondary': att.status === 'on_leave'
                      }">
                        {{$t(att.status)}}
                      </span>
                    </td>
                    <td class="py-5 px-6">
                      <button v-if="helper.checkAccess('edit_attendances') != -1" @click="edit(att)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" />
                        {{$t('edit')}}
                      </button>
                      <button v-if="helper.checkAccess('delete_attendances') != -1" @click="deleteAttendance(att)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" />
                        {{$t('delete')}}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="total > attendances.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
              <compact-pagination :pages="pagination" :current-page="currentPage" @change="changePage" />
            </div>

            <table-loading-bar v-if="loading" class="page-table-state text-center py-5" />
            <div v-else-if="!attendances.length && !isFiltered" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
              <AppIcon name="bi-calendar2-check" class="bi bi-calendar2-check text-muted" style="font-size: 3rem;" />
              <h4 class="mt-3">{{ $t('no_attendances_added') }}</h4>
              <p class="text-muted">{{ $t('start_by_adding_attendances') }}</p>
              <template v-if="helper.checkAccess('create_attendances') != -1">
                  <button class="btn btn-outline-primary mt-2" @click="create">
                      {{ $t('create_first_attendance') }}
                  </button>
              </template>
            </div>
            <div v-else-if="!attendances.length && isFiltered" class="page-table-state text-center py-5 text-muted">
               <AppIcon name="bi-inbox" class="bi bi-inbox fs-1 d-block mb-2" />
               {{$t('no_attendance_records')}}.
            </div>
          </div>
          <!-- table -->

          <div class="col-sm-7">
            {{$t('show')}}
            <select v-model="perPage" class="form-select form-select-sm d-inline-block w-auto mx-2" style="cursor:pointer;">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            {{$t('entities')}}
          </div>
        </div>
      </section>
    </div>

    <!-- Attendance Modal -->
    <div class="modal fade" id="attendanceModal" tabindex="-1" aria-labelledby="attendanceModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title" id="attendanceModalLabel">{{isEdit ? $t('edit_attendance') : $t('record_attendance')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="mb-3">
              <label class="form-label fw-bold small text-uppercase text-muted">{{$t('employee')}} <span class="text-danger">*</span></label>
              <select v-model="form.employee_id" class="form-select" :disabled="isEdit">
                <option value="">{{$t('select_employee')}}</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{emp.name}}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold small text-uppercase text-muted">{{$t('date')}} <span class="text-danger">*</span></label>
              <input type="date" v-model="form.date" class="form-control">
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('check_in')}}</label>
                <input type="time" v-model="form.check_in" @change="calculateWorkHours" class="form-control">
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('check_out')}}</label>
                <input type="time" v-model="form.check_out" @change="calculateWorkHours" class="form-control">
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold small text-uppercase text-muted">{{$t('work_hours')}}</label>
              <input type="number" step="0.5" v-model="form.work_hours" class="form-control bg-light" readonly>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold small text-uppercase text-muted">{{$t('status')}} <span class="text-danger">*</span></label>
              <select v-model="form.status" class="form-select">
                <option value="present">{{$t('present')}}</option>
                <option value="absent">{{$t('absent')}}</option>
                <option value="late">{{$t('late')}}</option>
                <option value="half_day">{{$t('half_day')}}</option>
                <option value="on_leave">{{$t('on_leave')}}</option>
              </select>
            </div>

            <div class="mb-3">
              <collapsible-notes-field v-model="form.notes" id="hr-attendance-notes" rows="2" :placeholder="$t('enter_notes')" />
            </div>
          </div>
          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{$t('cancel')}}</button>
            <button type="button" class="btn btn-success" @click="save">{{$t('save')}}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'
import sortableList from '@/mixins/sortableList'

export default {
  name: 'HrAttendance',
  persistedFilterState: {
    key: 'hr-attendance',
    fields: ['search', 'filters', 'perPage', 'sortField', 'sortDirection'],
  },
  mixins: [sortableList],
  data() {
    return {
      helper,
      attendances: [],
      employees: [],
      search: '',
      showFilters: false,
      filters: {
        from_date: '',
        to_date: '',
        status: ''
      },
      currentPage: 1,
      perPage: 10,
      total: 0,
      isEdit: false,
      form: this.getEmptyForm(),
      modal: null,
      loading: false,
      pagination: [],
      stats: { present: 0, absent: 0, late: 0, on_leave: 0 }
    }
  },
  computed: {
    isFiltered() {
      return (this.search && this.search.length > 0) ||
             (this.filters.from_date && this.filters.from_date !== '') ||
             (this.filters.to_date && this.filters.to_date !== '') ||
             (this.filters.status && this.filters.status !== '');
    }
  },
  watch: {
    search() {
      this.fetchAttendances()
    },
    'filters.from_date'() {
      this.fetchAttendances()
      this.fetchStats()
    },
    'filters.to_date'() {
      this.fetchAttendances()
      this.fetchStats()
    },
    'filters.status'() {
      this.fetchAttendances()
    },
    perPage() {
      this.fetchAttendances()
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_attendances') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_attendances') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchAttendances()
    this.fetchEmployees()
    this.fetchStats()
    // eslint-disable-next-line no-undef
    this.modal = new bootstrap.Modal(document.getElementById('attendanceModal'), {backdrop: 'static', keyboard: false})
  },
  methods: {
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    },
    async fetchStats() {
      try {
        let url = `${process.env.VUE_APP_API}/hr/insights/attendance-stats?`
        if (this.filters.from_date) url += `from_date=${this.filters.from_date}&`
        if (this.filters.to_date) url += `to_date=${this.filters.to_date}&`
        const response = await helper.customGet(url)
        this.stats = {
          present: response.data.present || 0,
          absent: response.data.absent || 0,
          late: response.data.late || 0,
          on_leave: response.data.on_leave || 0
        }
      } catch (error) {
        alert(error)
      }
    },
    getEmptyForm() {
      return {
        employee_id: '',
        date: new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Casablanca' }).format(new Date()),
        check_in: '',
        check_out: '',
        work_hours: 0,
        status: 'present',
        notes: ''
      }
    },
    formatDate(date) {
      return helper.formatDate(date)
    },
    formatTime(datetime) {
      if (!datetime) return '-'
      // Handle both full datetime string and time string
      if (datetime.includes('T') || datetime.includes(' ')) {
        const date = new Date(datetime)
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
      }
      return datetime.substring(0, 5)
    },
    async fetchEmployees() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employees?per_page=1000`)
        this.employees = response.data.data || []
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
      }
    },
    async fetchAttendances() {
      try {
        this.loading = true
        this.attendances = []
        
        const params = new URLSearchParams({ page: this.currentPage, per_page: this.perPage })
        this.appendSortParams(params)
        if (this.search) params.append('keyword', this.search)
        if (this.filters.from_date) params.append('from_date', this.filters.from_date)
        if (this.filters.to_date) params.append('to_date', this.filters.to_date)
        if (this.filters.status) params.append('status', this.filters.status)
        const url = `${process.env.VUE_APP_API}/employee-attendances?${params.toString()}`

        const response = await helper.customGet(url)
        
        this.attendances = response.data.data || []
        this.total = response.data.count || 0
        
        // Build pagination array
        this.pagination = []
        let maxPagination = Math.ceil(this.total / this.perPage)
        for(let i = 1; i <= maxPagination; i++){
          this.pagination.push(i)
        }
        
        this.loading = false
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
        this.attendances = []
        this.total = 0
        this.loading = false
      }
    },
    create() {
      this.isEdit = false
      this.form = this.getEmptyForm()
      this.modal.show()
    },
    edit(attendance) {
      this.isEdit = true
      this.form = {
        id: attendance.id,
        employee_id: attendance.employee_id,
        date: attendance.date,
        check_in: attendance.check_in ? new Date(attendance.check_in).toTimeString().slice(0, 5) : '',
        check_out: attendance.check_out ? new Date(attendance.check_out).toTimeString().slice(0, 5) : '',
        work_hours: attendance.work_hours || 0,
        status: attendance.status,
        notes: attendance.notes || ''
      }
      this.modal.show()
    },
    calculateWorkHours() {
      if (this.form.check_in && this.form.check_out) {
        const checkIn = new Date(`2000-01-01T${this.form.check_in}`)
        const checkOut = new Date(`2000-01-01T${this.form.check_out}`)
        const diffMs = checkOut - checkIn
        const diffHrs = diffMs / (1000 * 60 * 60)
        this.form.work_hours = diffHrs > 0 ? diffHrs.toFixed(2) : 0
      }
    },
    async save() {
      try {
        if (!this.form.employee_id || !this.form.date) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: this.$t('please_fill_required_fields')
          })
          return
        }

        const data = {
          employee_id: this.form.employee_id,
          date: this.form.date,
          check_in: this.form.check_in ? `${this.form.date}T${this.form.check_in}` : null,
          check_out: this.form.check_out ? `${this.form.date}T${this.form.check_out}` : null,
          status: this.form.status,
          notes: this.form.notes
        }

        this.loading = true
        if (this.isEdit) {
          await helper.customPatch(`${process.env.VUE_APP_API}/employee-attendances/${this.form.id}`, data, {showToast: true})
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/employee-attendances`, data, {showToast: true})
        }
        this.loading = false
        this.clearForm()
        this.modal.hide()
        this.fetchAttendances()
      } catch (error) {
        this.loading = false
      }
    },
    clearForm() {
      this.form = this.getEmptyForm()
    },
    async deleteAttendance(attendance) {
      const result = await this.$swal.fire({
        title: this.$t('are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('delete')
      })
      
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/employee-attendances/${attendance.id}`)
          this.attendances = helper.withoutDeletedRecords(this.attendances, attendance.id)
          this.total = Math.max(0, this.total - 1)
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },
    changePage(page) {
      this.currentPage = page
      this.fetchAttendances()
    }
  }
}
</script>

<style scoped>
.avatar-circle {
  transition: all 0.3s ease;
}
.table-responsive {
  overflow-x: auto;
}
.card {
  transition: transform 0.2s;
}
.card:hover {
  transform: translateY(-2px);
}
</style>
